import { useState } from 'react'
import { Link } from 'react-router-dom'
import useSWR from 'swr'
import { api } from '@/api'
import { ServiceCard } from '@/components/ServiceCard'

export default function Home() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const { data: servicesData } = useSWR('/services', () => api.services.getAll())
  const { data: categoriesData } = useSWR('/categories', () => api.services.getCategories())
  const { data: settingsData } = useSWR('/settings', () => api.settings.getAll())
  const { data: announcementsData } = useSWR('/announcements', () => api.announcements.getAll())

  const services = servicesData?.data || []
  const categories = categoriesData?.data || []
  const settings = settingsData?.data || {}
  const announcements = announcementsData?.data || []

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(s => s.category === selectedCategory)

  return (
    <div className="page-container pb-20">
      <header className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{settings.shop_name || '美丽美容院'}</h1>
            <p className="text-gray-600 text-sm">专业护理，让您更加美丽动人</p>
            {settings.business_hours && (
              <p className="text-xs text-gray-500 mt-2">营业时间：{settings.business_hours}</p>
            )}
          </div>
          <Link to="/admin" className="text-sm text-gray-400 hover:text-gray-600">
            管理 →
          </Link>
        </div>
      </header>

      {/* 店面公告 */}
      {announcements.length > 0 && (
        <section className="mb-6">
          <div className="bg-gradient-to-r from-primary-100 to-primary-50 rounded-2xl p-4 border border-primary-200">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold text-primary-800">店面公告</span>
            </div>
            <div className="space-y-2">
              {announcements.map(ann => (
                <div key={ann.id} className="text-sm bg-white bg-opacity-60 rounded-lg p-2 text-primary-900">
                  <span className="font-medium">{ann.title}: </span>
                  <span className="text-primary-700">{ann.content}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mb-6">
        <Link
          to="/booking"
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          立即预约
        </Link>
      </section>

      {settings.shop_phone && (
        <section className="mb-6">
          <a href={`tel:${settings.shop_phone}`} className="card-active block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">联系电话</p>
                <p className="font-semibold text-primary-600">{settings.shop_phone}</p>
              </div>
            </div>
          </a>
        </section>
      )}

      {settings.shop_address && (
        <section className="mb-6">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">店铺地址</p>
                <p className="text-sm text-gray-700">{settings.shop_address}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {categories.length > 0 && (
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">服务分类</h2>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-gray-700 border border-primary-200'
              }`}
            >
              全部
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-gray-700 border border-primary-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">服务项目</h2>
        <div className="space-y-3">
          {filteredServices.map(service => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
      </section>
    </div>
  )
}
